import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Target, Users, BookOpen, User, Heart, Compass, ArrowRight, ArrowLeft, Award } from 'lucide-react';
import GrainTexture from '@/components/GrainTexture';
import { cn } from '@/lib/utils';
import { SEO } from '@/components/SEO';
import OptimizedImage from '@/components/OptimizedImage';
import aanbodPanorama from '@/assets/systemique/aanbod-panorama.webp';

const aanbodFaq = [
  {
    id: 'verschil-opstelling-teamcoaching',
    question: 'Wat is het verschil tussen een organisatieopstelling en systemische teamcoaching?',
    answer:
      'Een organisatieopstelling maakt in 1,5 tot 2 uur de onderstroom van een vraagstuk zichtbaar via representanten of symbolen, denk aan ordening, plek en balans in het systeem. Systemische teamcoaching is een langer traject van meerdere sessies waarin we patronen, samenwerking en gedrag in het team verder doorwerken. Een opstelling levert vaak een doorbraak in inzicht; teamcoaching helpt om die inzichten in de praktijk te verankeren.',
  },
  {
    id: 'welke-vorm-past',
    question: 'Welke vorm past bij mijn vraag: opstelling, teamcoaching, leergang of 1-op-1?',
    answer:
      'Een organisatieopstelling past bij een concreet vraagstuk waar je snel inzicht in wilt. Systemische teamcoaching past bij een team dat structureel beter wil samenwerken. De systemische leergang voor leiders (Duiken in de Onderstroom) is voor leiders en adviseurs die zelf systemisch willen leren kijken en handelen. 1-op-1 coaching is bedoeld voor persoonlijke vraagstukken in je rol als leider of professional. Twijfel je welke vorm past? Plan een vrijblijvend kennismakingsgesprek, dan kijken we samen wat het beste aansluit.',
  },
  {
    id: 'voor-wie',
    question: 'Voor wie is dit aanbod bedoeld?',
    answer:
      'Het zakelijke aanbod is gericht op leiders, bestuurders, managers, HR-professionals, adviseurs en teams die met systemisch werk willen kijken naar organisatie- en samenwerkingsvraagstukken. Familieopstellingen voor particulieren lopen via systemique.nl. Marthe van der Molen werkt vanuit Castricum voor organisaties in heel Noord-Holland en daarbuiten, in-house of op locatie.',
  },
  {
    id: 'regio-in-house',
    question: 'In welke regio werken jullie en kan het ook in-house?',
    answer:
      'We werken vanuit Castricum voor organisaties in Amsterdam, Haarlem, Alkmaar, Zaandam, Hoofddorp en de rest van Noord-Holland en Utrecht. Trajecten en opstellingen kunnen op onze locatie of in-house bij jullie op kantoor plaatsvinden. Voor teamsessies en opstellingen komen we desgewenst door heel Nederland.',
  },
] as const;

// SpotlightCard Component
const SpotlightCard = ({ children, className = "", ...props }: any) => {
  const divRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [opacity, setOpacity] = useState(0);
  const [gradientSize, setGradientSize] = useState(600);

  React.useEffect(() => {
    const updateGradientSize = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setGradientSize(300);
      } else if (width < 1024) {
        setGradientSize(450);
      } else {
        setGradientSize(600);
      }
    };

    updateGradientSize();
    window.addEventListener('resize', updateGradientSize);
    return () => window.removeEventListener('resize', updateGradientSize);
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!divRef.current) return;
    const rect = divRef.current.getBoundingClientRect();
    setPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const handleFocus = () => { setOpacity(1); };
  const handleBlur = () => { setOpacity(0); };
  const handleMouseEnter = () => { setOpacity(1); };
  const handleMouseLeave = () => { setOpacity(0); };

  return (
    <div
      ref={divRef}
      onMouseMove={handleMouseMove}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={cn(
        "relative overflow-hidden rounded-2xl bg-white shadow-xl transition-all duration-300 hover:shadow-2xl border border-transparent hover:border-[#854C73]/20 h-full",
        className
      )}
      {...props}
    >
      <div
        className="pointer-events-none absolute -inset-px transition duration-300 z-0"
        style={{
          opacity,
          background: `radial-gradient(${gradientSize}px circle at ${position.x}px ${position.y}px, rgba(133, 76, 115, 0.15), transparent 40%)`,
        }}
      />
      <div className="relative z-10 h-full flex flex-col">{children}</div>
    </div>
  );
};

function Aanbod() {
  const services = [
    {
      title: "Organisatieopstellingen",
      desc: "Maak de onderstroom van je organisatie zichtbaar. In 1,5 tot 2 uur per casus krijg je scherp inzicht in patronen, plek en balans, ideaal bij hoog verloop, conflicten of vastgelopen verandering.",
      icon: Target,
      link: "/organisatieopstellingen",
      anchorText: "Organisatieopstellingen voor organisaties en teams"
    },
    {
      title: "Teamcoaching",
      desc: "Systemische teamcoaching voor teams die structureel beter willen samenwerken. Meerdere sessies waarin we patronen, rollen en de onderstroom van het team verder doorwerken.",
      icon: Users,
      link: "/teamcoaching",
      anchorText: "Systemische teamcoaching"
    },
    {
      title: "Systemische leergang voor leiders",
      desc: "3-daagse leergang Duiken in de Onderstroom. Ontwikkel systemisch leiderschap en leer pragmatisch systemisch kijken en handelen in je dagelijkse praktijk.",
      icon: Award,
      link: "/leergang-onderstroom",
      anchorText: "Duiken in de Onderstroom"
    },
    {
      title: "Opleidingstrajecten & maatwerk",
      desc: "Maatwerktrajecten voor teams en organisaties die systemisch werk structureel willen inbedden. Op locatie of in-house.",
      icon: BookOpen,
      link: "/opleidingstrajecten",
      anchorText: "Maatwerk opleidingen"
    },
    {
      title: "1-op-1 Coaching",
      desc: "Individuele begeleiding voor leiders en professionals met een persoonlijk vraagstuk in hun rol. Tafelopstellingen en vloerankers brengen je snel tot de kern.",
      icon: User,
      link: "/1-op-1",
      anchorText: "Individuele coaching"
    },
    {
      title: "Sluitertijd",
      desc: "Ontwikkelprogramma voor professionals",
      icon: Compass,
      link: "https://sluitertijd.com",
      anchorText: "Sluitertijd ontwikkelprogramma"
    }
  ];

  return (
    <>
      <SEO 
        title="Aanbod Organisatieopstellingen & Teamcoaching | Marthe van der Molen"
        description="Overzicht van het zakelijke aanbod: organisatieopstellingen, systemische teamcoaching, systemische leergang voor leiders en 1-op-1 begeleiding. Marthe van der Molen werkt vanuit Castricum, in-house of op locatie in heel Nederland."
        canonical="https://organisatieopstellingen.com/aanbod"
        ogImage="https://organisatieopstellingen.com/aanbod.png"
        ogTitle="Aanbod Organisatieopstellingen & Teamcoaching | Marthe van der Molen"
        ogDescription="Organisatieopstellingen, systemische teamcoaching, systemische leergang voor leiders en maatwerk voor teams."
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: aanbodFaq.map((item) => ({
              '@type': 'Question',
              name: item.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.answer,
              },
            })),
          }),
        }}
      />

    <div className="min-h-screen bg-[#FAEBEE]">
        {/* Custom Luxury Header */}
        <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center justify-center px-6 overflow-hidden bg-[#854C73]">
          <div className="absolute inset-0 z-0">
            <GrainTexture opacity={0.2} />
            <div className="absolute top-0 right-0 w-[300px] h-[300px] md:w-[500px] md:h-[500px] lg:w-[600px] lg:h-[600px] border border-[#F9EBEF]/10 rounded-full translate-x-1/2 -translate-y-1/2 opacity-30"></div>
          </div>
          
          <div className="container mx-auto relative z-10 text-center">
            <Button variant="ghost" asChild className="mb-8 hover:bg-white/20 text-white hover:text-white border border-white/30 mx-auto flex w-fit px-6 py-2 h-auto cursor-pointer">
              <Link to="/">
                <ArrowLeft className="mr-2 w-4 h-4" /> Terug naar home
              </Link>
            </Button>
            
            <h1 className="font-serif text-3xl md:text-5xl text-[#F9EBEF] mb-6">
              Het <span className="italic font-light font-serif text-[#F9EBEF]">Aanbod</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-[#F9EBEF]/80 font-serif italic max-w-2xl mx-auto">
              Organisatieopstellingen, teamcoaching en systemische leergang voor leiders.
            </p>
          </div>
        </section>
        
        <section className="px-4 sm:px-6 pt-10 sm:pt-14 bg-[#FAEBEE]">
          <div className="max-w-2xl mx-auto">
            <div className="group rounded-2xl overflow-hidden shadow-md">
              <div className="aspect-[21/9] overflow-hidden">
                <OptimizedImage
                  src={aanbodPanorama}
                  webpSrc={aanbodPanorama}
                  alt="Systemische opstelling met houten representanten, organisatieopstellingen"
                  className="w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                  width={1600}
                  height={1067}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Intro: indexeerbare body copy */}
        <section className="py-12 md:py-16 px-6 bg-[#FAEBEE]">
          <div className="container mx-auto max-w-3xl text-center">
            <h2 className="font-serif text-2xl md:text-3xl text-[#854C73] mb-6">
              Welk systemisch aanbod past bij <span className="italic font-light">jouw vraag</span>?
            </h2>
            <p className="text-[#6b4912] text-lg leading-relaxed font-light mb-5">
              Op deze site vind je het zakelijke aanbod van Marthe van der Molen: organisatieopstellingen,
              systemische teamcoaching en systemische leergang voor leiders. Patronen, plek en balans in een team of organisatie
              bepalen vaak meer dan wat op papier staat.
            </p>
            <p className="text-[#6b4912] text-lg leading-relaxed font-light">
              Niet zeker welke vorm past? Begin met een{' '}
              <Link to="/contact" className="text-[#854C73] underline underline-offset-4 hover:text-[#590D0D]">
                vrijblijvend kennismakingsgesprek
              </Link>
              . Voor familieopstellingen en particulieren verwijzen we je naar{' '}
              <a
                href="https://www.systemique.nl/aanbod"
                className="text-[#854C73] underline underline-offset-4 hover:text-[#590D0D]"
                target="_blank"
                rel="noopener noreferrer"
              >
                systemique.nl
              </a>
              .
            </p>
            <div className="mt-10 overflow-x-auto text-left">
              <table className="w-full min-w-[540px] text-sm md:text-base border-collapse">
                <caption className="sr-only">Vergelijking organisatieopstelling, teamcoaching en leergang</caption>
                <thead>
                  <tr className="border-b border-[#854C73]/20">
                    <th scope="col" className="py-3 pr-4 text-[#854C73] font-serif font-normal text-left">Vorm</th>
                    <th scope="col" className="py-3 px-4 text-[#854C73] font-serif font-normal text-left">Organisatieopstelling</th>
                    <th scope="col" className="py-3 px-4 text-[#854C73] font-serif font-normal text-left">Teamcoaching</th>
                    <th scope="col" className="py-3 pl-4 text-[#854C73] font-serif font-normal text-left">Leergang</th>
                  </tr>
                </thead>
                <tbody className="text-[#6b4912]">
                  <tr className="border-b border-[#854C73]/10">
                    <th scope="row" className="py-3 pr-4 font-medium text-left">Duur</th>
                    <td className="py-3 px-4">1,5–2 uur per casus</td>
                    <td className="py-3 px-4">1–6 sessies (2–3 uur)</td>
                    <td className="py-3 pl-4">3 dagen</td>
                  </tr>
                  <tr className="border-b border-[#854C73]/10">
                    <th scope="row" className="py-3 pr-4 font-medium text-left">Doel</th>
                    <td className="py-3 px-4">Inzicht in systeemdynamiek</td>
                    <td className="py-3 px-4">Teamontwikkeling &amp; onderstroom</td>
                    <td className="py-3 pl-4">Systemisch leiderschap leren</td>
                  </tr>
                  <tr className="border-b border-[#854C73]/10">
                    <th scope="row" className="py-3 pr-4 font-medium text-left">Groep</th>
                    <td className="py-3 px-4">Casus + representanten</td>
                    <td className="py-3 px-4">Heel team</td>
                    <td className="py-3 pl-4">Max. 8 deelnemers</td>
                  </tr>
                  <tr>
                    <th scope="row" className="py-3 pr-4 font-medium text-left">Investering</th>
                    <td className="py-3 px-4">Op maat</td>
                    <td className="py-3 px-4">Op maat</td>
                    <td className="py-3 pl-4">€ 1.500 excl. btw</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>
        
        {/* Services Grid with Spotlight Cards */}
        <section className="py-12 md:py-24 px-6 relative">
          <GrainTexture opacity={0.1} />
          <div className="container mx-auto max-w-7xl relative z-10">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => {
                const Icon = service.icon;
                const isExternal = service.link.startsWith('http');
                const CardContent = (
                  <SpotlightCard className="p-10">
                    <div className="mb-8 transform group-hover:scale-110 transition-transform duration-500 origin-left">
                      <Icon size={48} strokeWidth={1} className="text-[#854C73]" />
                    </div>
                    
                    <h3 className="font-serif text-2xl md:text-3xl text-[#854C73] mb-4 group-hover:text-[#590D0D] transition-colors leading-tight break-words">
                      {service.title}
                    </h3>
                    
                    <p className="text-gray-600 text-lg leading-relaxed mb-10 flex-grow font-light">
                      {service.desc}
                    </p>
                    
                    <div className="flex items-center text-[#854C73] font-bold text-xs tracking-[0.2em] uppercase mt-auto">
                      <span className="border-b-2 border-[#854C73]/20 group-hover:border-[#854C73] transition-all duration-300 pb-1">
                        {service.anchorText}
                      </span> 
                      <ArrowRight size={16} className="ml-2 transform group-hover:translate-x-2 transition-transform duration-300" />
                    </div>
                  </SpotlightCard>
                );
                
                return isExternal ? (
                  <a key={index} href={service.link} target="_blank" rel="noopener noreferrer" className="group block h-full">
                    {CardContent}
                  </a>
                ) : (
                  <Link key={index} to={service.link} className="group block h-full">
                    {CardContent}
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="pb-16 md:pb-24 px-4 sm:px-6">
          <div className="container mx-auto max-w-3xl">
            <div className="text-center mb-10 md:mb-12">
              <h2 className="font-serif text-3xl md:text-4xl text-[#854C73] mb-3">
                Veelgestelde <span className="italic font-light font-serif">vragen</span>
              </h2>
              <p className="text-[#6b4912] text-base md:text-lg">
                Kort antwoord op de meest gestelde vragen over het zakelijke aanbod.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-[#854C73]/10 px-6 md:px-8 py-2 divide-y divide-[#854C73]/10">
              {aanbodFaq.map((item) => (
                <article key={item.id} className="py-6 first:pt-4 last:pb-4">
                  <h3 className="font-serif text-[#854C73] text-base md:text-lg mb-3">
                    {item.question}
                  </h3>
                  <p className="text-[#6b4912] leading-relaxed text-sm md:text-base">
                    {item.answer}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export default Aanbod;